import React, { useEffect, useRef, useState } from 'react';
import { Block, CtaButtonBlockContent } from '../types';
import { LinkControlPanelInputForm } from '../links/link-input-form';
import { shallow } from 'zustand/shallow';
import { useClipboardStore } from '../utils/clipboard.store';
import { EmbedCheckResponse } from '../links/types';
import { debounce } from 'lodash';
import { Nullable } from 'src/types/nullable.type';
import { ThemeColorPicker } from 'src/document-editor/neue-text-menu-theme';
import { functionNoop } from 'src/utils/function/noop';
import classNames from 'classnames';

interface Props {
  block: Block;
  onNewUrl: (url: string, title: string, checkResponse?: EmbedCheckResponse, isCTA?: boolean) => void;
  onTextChange: (text: string) => void;
  onColorsChange: (colors: Partial<{ backgroundColor: string; textColor: string }>) => void;
}

export const AddCtaButtonPanelContent = ({ block, onNewUrl, onTextChange, onColorsChange }: Props) => {
  const blockContent = block.content as CtaButtonBlockContent;
  const [text, setText] = React.useState(blockContent.text);

  const [shownWidget, setShownWidget] = useState<Nullable<'bg-color' | 'text-color'>>(null);

  const bgColorCloseRef = useRef(functionNoop);
  const textColorCloseRef = useRef(functionNoop);

  const currentClipboardData = useClipboardStore((state) => state.currentClipboardData, shallow);
  const clipboardAutoGenerate = useClipboardStore((state) => state.autoGenerate, shallow);
  const revokeAutoGenerate = useClipboardStore((state) => state.revokeAutoGenerate);

  const debouncedTextChanged = debounce(onTextChange, 500);

  useEffect(() => {
    debouncedTextChanged(text);
    return () => {
      debouncedTextChanged.cancel();
    };
  }, [text]);

  return (
    <div className='flex flex-col gap-4 relative cta-button-colors'>
      <div
        className={classNames(
          'fixed z-[9999] right-[calc(100%+8px)] top-0 rounded-lg bg-neue-journey-bg-90 text-neue-journey-fg p-4',
          {
            hidden: shownWidget !== 'text-color',
          }
        )}
      >
        <ThemeColorPicker
          text='Select'
          color={blockContent.textColor || '#F55722'}
          onColorChange={(color) => {
            onColorsChange({ textColor: color });
          }}
          onAddColor={(color) => {
            onColorsChange({ textColor: color });
            textColorCloseRef.current();
            setShownWidget(null);
          }}
          closeRef={textColorCloseRef}
        />
      </div>
      <div
        className={classNames(
          'fixed z-[9999] right-[calc(100%+8px)] top-0 rounded-lg bg-neue-journey-bg-90 text-neue-journey-fg p-4',
          {
            hidden: shownWidget !== 'bg-color',
          }
        )}
      >
        <ThemeColorPicker
          text='Select'
          color={blockContent.backgroundColor || '#F55722'}
          onColorChange={(color) => {
            onColorsChange({ backgroundColor: color });
          }}
          onAddColor={(color) => {
            onColorsChange({ backgroundColor: color });
            bgColorCloseRef.current();
            setShownWidget(null);
          }}
          closeRef={bgColorCloseRef}
        />
      </div>
      <div className='flex items-center gap-2 justify-between'>
        <span className='text-neue-journey-medium select-none'>Background color</span>
        <svg
          className='w-[18px] h-[18px] py-0.5 text-neue-journey-fg bg-neue-journey-fg-10 cursor-pointer rounded-2xl'
          viewBox='0 0 16 16'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
          style={{ backgroundColor: blockContent.backgroundColor }}
          onClick={() => {
            setShownWidget('bg-color');
          }}
        />
      </div>
      <div className='flex items-center gap-4 justify-between'>
        <span className='text-neue-journey-medium select-none'>Text color</span>
        <svg
          className='w-[18px] h-[18px] py-0.5 text-neue-journey-fg bg-neue-journey-fg-10 cursor-pointer rounded-2xl'
          viewBox='0 0 16 16'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
          style={{ backgroundColor: blockContent.textColor }}
          onClick={() => {
            setShownWidget('text-color');
          }}
        />{' '}
      </div>
      <div className='flex flex-col gap-2'>
        <label htmlFor='title' className='text-neue-journey-medium text-neue-journey-fg-90'>
          Button text
        </label>
        <input
          id='title'
          type='text'
          value={text}
          placeholder='Button text'
          className='neue-journey-text-input'
          onChange={(e) => {
            setText(e.target.value);
          }}
        />
      </div>
      <div className='flex flex-col gap-2'>
        <label htmlFor='url' className='text-neue-journey-medium text-neue-journey-fg-90'>
          Button URL
        </label>
        <LinkControlPanelInputForm
          url={blockContent.target || ''}
          isUrlFromClipboard={!!currentClipboardData && clipboardAutoGenerate}
          onNewUrl={(url) => {
            let urlWithProtocol = url;

            // Check if the URL is a special case for Outlook booking
            const isOutlookBookingUrl = urlWithProtocol.includes('outlook.office365.com/book');
            
            if (!isOutlookBookingUrl && /^\S+@\S+\.\S+$/.test(urlWithProtocol) && !urlWithProtocol.startsWith('mailto')) {
              urlWithProtocol = 'mailto:' + urlWithProtocol;
            } else if (!urlWithProtocol.startsWith('http') && !urlWithProtocol.startsWith('mailto')) {
              urlWithProtocol = 'https://' + urlWithProtocol;
            }
            onNewUrl(urlWithProtocol, '', currentClipboardData?.embedCheckResponse, true);
          }}
          onSubmitClipboardForm={revokeAutoGenerate}
          placeholder='Paste your URL or e-mail'
          autoFocus
        />
      </div>
    </div>
  );
};